import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card, Form, Row, Col, Spinner, Button, Container, Alert } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";

const EditStaff = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    username: "",
    mobileno: "",
    password: "", // placeholder to indicate no change
    gender: "",
    role: null,
    department: null
  });

  const [errors, setErrors] = useState({
    email: "",
    mobileno: ""
  });

  const [touched, setTouched] = useState({
    email: false,
    mobileno: false
  });

  const [roleOptions, setRoleOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch staff data and options
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [staffRes, rolesRes, depsRes] = await Promise.all([
          axios.get(`${BASE_URL}staff/findById/${id}`, { headers: authHeader() }),
          axios.get(`${BASE_URL}role/findAllRole`, { headers: authHeader() }),
          axios.get(`${BASE_URL}department/all`, { headers: authHeader() })
        ]);

        const staff = staffRes.data;

        setRoleOptions(
          rolesRes.data.map(role => ({
            value: role.id,
            label: role.role
          }))
        );

        setDepartmentOptions(
          depsRes.data.map(dep => ({
            value: dep.id,
            label: dep.name
          }))
        );

        setFormData({
          firstname: staff.firstname || "",
          lastname: staff.lastname || "",
          email: staff.email || "",
          username: staff.username || "",
          mobileno: staff.mobileno || "",
          password: staff.password || "",
          gender: staff.gender || "",
          role: staff.roles?.[0] ? {
            value: staff.roles[0].id,
            label: staff.roles[0].role
          } : null,
          department: staff.department ? {
            value: staff.department.id,
            label: staff.department.name
          } : null
        });

      } catch (err) {
        setError("Failed to load staff data. Please try again.");
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Validate on change if field was touched
    if (touched[name]) {
      validateField(name, value);
    }
  };

  // Handle blur (when field loses focus)
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  // Field validation
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "email":
        if (!value) {
          error = "Email is required";
        } else if (!/^\S+@\S+\.\S+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "mobileno":
        if (!value) {
          error = "Mobile number is required";
        } else if (!/^\d{10}$/.test(value)) {
          error = "Please enter a 10-digit mobile number";
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  // Handle select changes
  const handleSelectChange = (name, selectedOption) => {
    setFormData(prev => ({ ...prev, [name]: selectedOption }));
  };

  // Form validation
  const validateForm = () => {
    // Validate all fields first
    let isValid = true;

    if (!formData.firstname) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "First Name is required"
      });
      return false;
    }

    if (!formData.lastname) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Last Name is required"
      });
      return false;
    }

    if (!validateField("email", formData.email)) {
      isValid = false;
    }

    if (!formData.username) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Username is required"
      });
      return false;
    }

    if (!validateField("mobileno", formData.mobileno)) {
      isValid = false;
    }

    if (!formData.gender) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Gender is required"
      });
      return false;
    }

    if (!formData.role) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Role is required"
      });
      return false;
    }

    if (!formData.department) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Department is required"
      });
      return false;
    }

    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched to show errors
    setTouched({
      email: true,
      mobileno: true
    });

    if (!validateForm()) return;
    setIsSubmitting(true);

    const payload = {
      id,
      firstname: formData.firstname,
      lastname: formData.lastname,
      email: formData.email,
      username: formData.username,
      mobileno: formData.mobileno,
      gender: formData.gender,
      roles: [{ id: formData.role.value }],
      department: { id: formData.department.value }
    };

    payload.password = formData.password;

    try {
      await axios.put(`${BASE_URL}staff/update/${id}`, payload, {
        headers: authHeader()
      });

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Staff member updated successfully.',
        timer: 2000
      });

      // navigate("/staffindex/1");
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Failed to update staff member.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card className="shadow">
        <Card.Header className=" text-primary d-flex justify-content-between align-items-center">
          <h4>Update User Member</h4>
          <Link to="/staffindex/1" className="btn btn-light">
            <i className="fas fa-arrow-left me-2"></i> Back to List
          </Link>
        </Card.Header>

        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="firstname" className="mb-3">
                  <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="lastname" className="mb-3">
                  <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="email" className="mb-3">
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && !!errors.email}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                  {!errors.email && touched.email && (
                    <Form.Text className="text-success">
                      Email looks good!
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="username" className="mb-3">
                  <Form.Label>Username <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="mobileno" className="mb-3">
                  <Form.Label>Mobile Number <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    name="mobileno"
                    value={formData.mobileno}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.mobileno && !!errors.mobileno}
                    required
                    maxLength="10"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.mobileno}
                  </Form.Control.Feedback>
                  {!errors.mobileno && touched.mobileno && (
                    <Form.Text className="text-success">
                      Mobile number looks good!
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formPassword" className="mb-3">
                  <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password ? "********" : ""}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        password: newValue === "********" ? prev.password : newValue
                      }));
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="gender" className="mb-3">
                  <Form.Label>Gender <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="role" className="mb-3">
                  <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                  <Select
                    value={formData.role}
                    onChange={(option) => handleSelectChange('role', option)}
                    options={roleOptions}
                    placeholder="Select Role"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="department" className="mb-3">
                  <Form.Label>Department <span className="text-danger">*</span></Form.Label>
                  <Select
                    value={formData.department}
                    onChange={(option) => handleSelectChange('department', option)}
                    options={departmentOptions}
                    placeholder="Select Department"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end">
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting}
                className="px-4"
              >
                {isSubmitting ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    Update Staff
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EditStaff;